import { useRef, useState, useEffect } from 'react';
import {
  Wrapper,
  Suggestions,
  SuggestionItem,
  FillBorder,
  SuggestionItemPlaceholder,
} from './popover.style';
import { IPopoverProps } from './popover.models';

/** POPOVER DOCS
 * @description This component aims to display an array of google.maps.places.AutocompletePrediction strings passed from "props.items" as a list below the
 * "props.target" ReactNode componet.
 * @param props -
 * - items : google.maps.places.AutocompletePrediction[] --> (REQUIRED) array of suggestion to be displayed below target component
 * - target : ReactNode --> (REQUIRED) item to be wrapped by Popover
 * - isDisable : boolean --> (REQUIRED) to handle popover view
 * - handlePlaceSelect : (google.maps.places.AutocompletePrediction) => void --> (REQUIRED) to handle suggestion select
 * @author Andrea
 */

const Popover = (props: IPopoverProps) => {
  const { target, items, isDisabled, placeholder, handlePlaceSelect, onClose } = props;
  const placeholderRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const optionRefs = useRef<Array<HTMLElement | null>>([]);
  const sliced = items?.slice(0, 4) || [];

  const optionId = (i: number) => `popover-option-${sliced[i]?.place_id || i}`;

  const moveFocusToIndex = (nextIndex: number) => {
    const clamped = Math.max(0, Math.min(nextIndex, sliced.length - 1));
    setActiveIndex(clamped);
    const el = optionRefs.current[clamped];
    el?.focus?.();
  };

  const onListboxKeyDown = (e: React.KeyboardEvent) => {
    if (!sliced.length) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = activeIndex < 0 ? 0 : activeIndex + 1;
        moveFocusToIndex(next >= sliced.length ? 0 : next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = activeIndex < 0 ? 0 : activeIndex - 1;
        moveFocusToIndex(prev < 0 ? sliced.length - 1 : prev);
        break;
      }
      case 'Home': {
        e.preventDefault();
        moveFocusToIndex(0);
        break;
      }
      case 'End': {
        e.preventDefault();
        moveFocusToIndex(sliced.length - 1);
        break;
      }
      case 'Enter':
      case ' ': {
        if (activeIndex >= 0 && activeIndex < sliced.length) {
          e.preventDefault();
          handlePlaceSelect(sliced[activeIndex]);
        }
        break;
      }
      case 'Tab': {
        // if placeholder is available and active is last, move focus to it.
        if (
          !e.shiftKey &&
          activeIndex === sliced.length - 1 &&
          placeholder?.enabled &&
          placeholder.label
        ) {
          e.preventDefault();
          placeholderRef.current?.focus?.();
        }
        // otherwise allow default tabbing out, which will close popover via outside click/blur.
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!isDisabled && sliced.length) setActiveIndex(0);
    else setActiveIndex(-1);
  }, [isDisabled, sliced.length]);
  return (
    <div>
      {target}
      {items && !isDisabled && (
        <>
          <FillBorder />
          <Wrapper>
            <Suggestions
              role="listbox"
              aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
              ref={listboxRef as any}
              tabIndex={-1}
              onKeyDown={onListboxKeyDown}
              onFocus={() => {
                // move focus to the first option:
                if (sliced.length > 0) {
                  if (activeIndex === -1 && sliced.length > 0) {
                    setActiveIndex(0);
                    optionRefs.current[0]?.focus();
                  }
                }
              }}
            >
              {sliced.map((el, index) => (
                <SuggestionItem
                  id={optionId(index)}
                  role="option"
                  aria-selected={activeIndex === index}
                  // tabIndex={index === 0 ? 0 : -1}
                  tabIndex={activeIndex === index ? 0 : -1}
                  data-cs-mask=""
                  key={el.place_id}
                  ref={(node: any) => (optionRefs.current[index] = node)}
                  onClick={() => {
                    handlePlaceSelect(el);
                  }}
                  onFocus={() => {
                    if (activeIndex !== index) setActiveIndex(index);
                  }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = index + 1 >= sliced.length ? 0 : index + 1;
                      moveFocusToIndex(next);
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prev = index - 1 < 0 ? sliced.length - 1 : index - 1;
                      moveFocusToIndex(prev);
                      return;
                    }
                    if (e.key === 'Home') {
                      e.preventDefault();
                      moveFocusToIndex(0);
                      return;
                    }
                    if (e.key === 'End') {
                      e.preventDefault();
                      moveFocusToIndex(sliced.length - 1);
                      return;
                    }
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePlaceSelect(el);
                      return;
                    }
                    if (e.key === 'Tab') {
                      // tab moves to placeholder if present; final Tab should close
                      if (
                        !e.shiftKey &&
                        index === sliced.length - 1 &&
                        placeholder?.enabled &&
                        placeholder.label
                      ) {
                        e.preventDefault();
                        placeholderRef.current?.focus?.();
                        return;
                      }
                    }
                  }}
                >
                  {el.description}
                </SuggestionItem>
              ))}
              {placeholder?.enabled && placeholder.label && (
                <SuggestionItemPlaceholder
                  ref={placeholderRef as any}
                  tabIndex={0}
                  onClick={placeholder.action}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      placeholder.action?.();
                    }
                    if (e.key === 'Tab' && !e.shiftKey) {
                      e.preventDefault();
                      onClose && onClose(); //call onClose passed as prop by parent component
                      (e.currentTarget as HTMLElement).blur();
                    }
                  }}
                >
                  {placeholder.label}
                </SuggestionItemPlaceholder>
              )}
            </Suggestions>
          </Wrapper>
        </>
      )}
    </div>
  );
};

export default Popover;

